import React, { FormEvent, useEffect, useRef, useState } from "react";
import OtpInput from "react-otp-input";
import Typography from "@carrier-io/air-react/Typography";
import Box from "@carrier-io/air-react/Box";
import {
  LoginCodeMutation,
  SendAuthCodeMutation,
} from "../../graphql/mutations/loginCode";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  deleteSessionId,
  setLoginCookies,
  setSessionId,
} from "../../utils/cookie";
import { RoutesPage } from "../../constants/routes";
import { Clients, useMutation } from "../../apollo";
import { OtpInputItem } from "../../components/otpInputItem/OtpInputItem";
import Cookie from "js-cookie";
import { Button } from "../../components/button/Button";

export const ConfirmAuthChallenge = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const username = searchParams.get("username");
  const region = searchParams.get("region");

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const sessionId = useRef<string>(Cookie.get("sessionId") || "");

  const [sendAuthCode, { loading: sendAuthCodeLoading }] = useMutation(
    Clients.Authn,
    SendAuthCodeMutation,
  );

  const [verifyAuthCode, { loading: validateAutoCodeLoading }] = useMutation(
    Clients.Authn,
    LoginCodeMutation,
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!username || !region || !email || !region) {
      navigate({ pathname: RoutesPage.Login, search: searchParams.toString() });
    }
  }, [username, region, email, navigate, searchParams]);

  useEffect(() => {
    (async () => {
      if (!sessionId.current) {
        try {
          const result = await sendAuthCode({
            variables: {
              username,
              region,
            },
          });

          await handleAuthSession(result.data.authRequestCode.sessionId);
        } catch (error) {
          setError("Error while sending auth code");
        }
      }
    })();
  }, [sendAuthCode, setError, sessionId, username, region]);

  const handleAuthSession = async (id: string) => {
    setSessionId(id, window.location.hostname);
    sessionId.current = id;
    setError("");
  };

  const resetSession = () => {
    deleteSessionId(window.location.hostname);
    sessionId.current = "";
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedText)) {
      setCode(pastedText);
    }
  };

  const isButtonDisable = code.length !== 6;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const result = await verifyAuthCode({
        variables: {
          username,
          region,
          email,
          sessionId: sessionId.current,
          code,
        },
      });

      const { idToken, accessToken, refreshToken } = result.data.authVerifyCode;
      setLoginCookies(
        { idToken, accessToken, refreshToken },
        window.location.hostname,
      );
      resetSession();
      navigate({
        pathname: RoutesPage.ConsentModal,
        search: searchParams.toString(),
      });
    } catch (error) {
      setError("Authorization code invalid");
    }
  };

  const handleResentAuthCode = async () => {
    try {
      resetSession();
      const result = await sendAuthCode({
        variables: {
          username,
          region,
        },
      });
      await handleAuthSession(result.data.authRequestCode.sessionId);
      setCode("");
    } catch (error) {
      setError("Error while sending auth code");
    }
  };

  return (
    <>
      <Typography
        sx={{
          marginTop: 3,
          marginBottom: 3,
        }}
        variant="body2"
        align="center"
      >
        Check your email for a one-time passcode and enter it below to continue:
      </Typography>
      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <OtpInput
            containerStyle={{
              width: "100%",
              justifyContent: "space-between",
            }}
            value={code}
            onChange={setCode}
            onPaste={handlePaste}
            inputType="number"
            numInputs={6}
            shouldAutoFocus
            renderInput={(props, index) => (
              <OtpInputItem
                {...props}
                data-testid={`otp-input-${index}`}
                disabled={Boolean(error)}
                style={{
                  fontWeight: 400,
                  fontSize: 32,
                  textAlign: "center",
                  verticalAlign: "middle",
                  border: error ? "1px solid #d32f2f" : undefined,
                  color: error ? "#999D9F" : undefined,
                }}
              />
            )}
          />
        </Box>
        <Typography
          data-testid="validation-error"
          variant="body2"
          color="error"
          padding="16px 0"
          textAlign="center"
        >
          {error}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            width: "100%",
          }}
        >
          {error ? (
            <Button
              hidden={!error}
              fullWidth
              variant="outlined"
              className={"styles.submitButton"}
              onClick={handleResentAuthCode}
              isLoading={sendAuthCodeLoading}
            >
              Request a new code
            </Button>
          ) : (
            ""
          )}
          <Button
            type="submit"
            isLoading={validateAutoCodeLoading}
            disabled={isButtonDisable || validateAutoCodeLoading || !!error}
          >
            Continue
          </Button>
        </Box>
      </form>
      <Button
        data-testid="back"
        variant="text"
        color="inherit"
        className={"styles.submitButton"}
        onClick={() => {
          deleteSessionId(window.location.hostname);
          navigate({
            pathname: RoutesPage.Login,
            search: searchParams.toString(),
          });
        }}
        fullWidth
      >
        Cancel
      </Button>
    </>
  );
};
